// All customer-facing copy for the Learn Phoebe page. Each task is a short
// scripted conversation played in the fake chat; scripts are cross-checked
// against actual agent behavior. Keep claims scoped when editing:
//
//   - Coverage outreach is a two-approval lifecycle. `draft_agent_outreach`
//     CREATES the outreach ("Create this outreach?" — a shift count, no
//     message preview, nobody contacted yet), recommendations are then
//     computed asynchronously and posted back unprompted, and a second card
//     ("Start this outreach?" — a caregiver count) actually dials and texts.
//     Never script an outreach card that previews an SMS body.
//   - A start time folds the send into the first card: approving it also
//     approves the future send, and no second card appears when it fires.
//   - Only one-off texts and caregiver notifications preview a message body.
//   - Memory notes save without an approval card.
//   - The @ picker finds shifts by CLIENT name (never imply "@Saturday").
//
// Keep Phoebe replies under ~120 characters so each reply finishes typing
// before the next turn is revealed. Tips should give the reader a concrete
// idea of what to ask, not restate the title.

// The customer-facing product docs (Kratos-gated, same login as the app).
export const PRODUCT_DOCS_URL = 'https://docs.phoebe.work';

export type PromptSegment =
    | { readonly text: string }
    | { readonly slot: string };

export type SlottedPrompt = ReadonlyArray<PromptSegment>;

export function promptPlainText(prompt: SlottedPrompt): string {
    return prompt
        .map((segment) =>
            'slot' in segment ? `[${segment.slot}]` : segment.text,
        )
        .join('');
}

export type MentionPillKind = 'caregiver' | 'client' | 'shift';

export type MentionPillData = {
    readonly kind: MentionPillKind;
    readonly label: string;
    // Text typed into the composer before the picker opens; defaults to
    // '@' plus the first letters of the label. Shift pills override it
    // because the picker finds shifts by the client's name.
    readonly query?: string;
    // Client name shown on a shift's picker row (the real picker lists
    // shifts as client name + date and time).
    readonly pickerClientName?: string;
};

export type UserSegment =
    | { readonly text: string }
    | { readonly pill: MentionPillData };

export type ChatTurn =
    | { readonly kind: 'user'; readonly segments: ReadonlyArray<UserSegment> }
    | {
          readonly kind: 'phoebe';
          readonly text: string;
          readonly lines?: ReadonlyArray<string>;
      }
    // Mirrors a real approval card: a title, a summary of what Phoebe will
    // do, and a quoted message ONLY where the real card previews one (texts
    // and caregiver notifications). Outreach cards carry no message preview —
    // see APPROVAL_COPY_BUILDERS in the chat view.
    | {
          readonly kind: 'approval';
          readonly title: string;
          readonly body: string;
          readonly preview?: string;
      }
    | { readonly kind: 'chip'; readonly text: string }
    | {
          readonly kind: 'artifact';
          readonly title: string;
          readonly meta: string;
      }
    // A short guided-walkthrough video Phoebe returns for how-to questions.
    | {
          readonly kind: 'video';
          readonly title: string;
          readonly durationLabel: string;
      }
    | { readonly kind: 'divider'; readonly text: string };

export type LearnTask = {
    readonly id:
        | 'mention'
        | 'fill'
        | 'callout'
        | 'text'
        | 'outreach'
        | 'scheduled'
        | 'report'
        | 'memory'
        | 'watch'
        | 'walkthrough'
        | 'sync';
    readonly title: string;
    readonly tip: string;
    readonly prompt: SlottedPrompt;
    // When set, the picker mockup also shows a Shifts group with this row
    // (the @ task uses it to teach that shifts are found by client name).
    readonly pickerShiftGroupRow?: {
        readonly clientName: string;
        readonly label: string;
    };
    readonly turns: ReadonlyArray<ChatTurn>;
};

export const LEARN_TASKS: ReadonlyArray<LearnTask> = [
    {
        id: 'mention',
        title: 'Pick a record with @',
        tip: 'Type @ to pick the exact caregiver, client, or shift.',
        pickerShiftGroupRow: {
            clientName: 'Rosa Y.',
            label: 'Jul 11, 8:00 AM',
        },
        prompt: [
            { text: 'Find all shifts this week for ' },
            { slot: '@ pick a client' },
        ],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Find all shifts this week for ' },
                    { pill: { kind: 'client', label: 'Rosa Y.' } },
                ],
            },
            {
                kind: 'phoebe',
                text:
                    'Rosa has 3 shifts this week. Saturday 8:00 AM to ' +
                    '4:00 PM still needs coverage.',
            },
        ],
    },
    {
        id: 'text',
        title: 'Send a one-off text or call',
        tip: 'Texts go out in the caregiver’s language.',
        prompt: [
            { text: 'Text ' },
            { slot: '@ pick a caregiver' },
            { text: ' good morning' },
        ],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Text ' },
                    { pill: { kind: 'caregiver', label: 'Mara T.' } },
                    { text: ' good morning' },
                ],
            },
            {
                kind: 'approval',
                title: 'Send this text?',
                body: 'Phoebe will text this caregiver:',
                preview: '“Good morning Mara!”',
            },
            { kind: 'chip', text: 'Sent in the caregiver’s language' },
        ],
    },
    {
        id: 'sync',
        title: 'Sync EHR data',
        tip: 'Ask Phoebe to sync EHR data. Pull everything, or just one client, caregiver, or shift.',
        prompt: [{ text: 'Sync EHR data for ' }, { slot: '@ pick a client' }],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Sync EHR data for ' },
                    { pill: { kind: 'client', label: 'Rosa Y.' } },
                ],
            },
            {
                kind: 'approval',
                title: 'Sync from the EHR?',
                body: 'Phoebe will pull the latest data from the EHR.',
            },
            { kind: 'chip', text: 'Sync started' },
            {
                kind: 'phoebe',
                text: 'Rosa is up to date. Org-wide syncs take a few minutes.',
            },
        ],
    },
    {
        id: 'walkthrough',
        title: 'Get a guided walkthrough',
        tip: 'Ask anything about how the product works.',
        prompt: [{ text: 'How do I assign and fill an open shift?' }],
        turns: [
            {
                kind: 'user',
                segments: [{ text: 'How do I assign and fill an open shift?' }],
            },
            {
                kind: 'phoebe',
                text:
                    'Once a caregiver confirms interest, you assign and ' +
                    'fill the shift right from that outreach:',
            },
            {
                kind: 'video',
                title: 'Assign and fill an open shift',
                durationLabel: '1:12',
            },
        ],
    },
    {
        id: 'report',
        title: 'Create a downloadable report',
        tip: 'The report appears in chat, ready to download.',
        prompt: [{ text: 'Create an outreach report for the last 7 days' }],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Create an outreach report for the last 7 days' },
                ],
            },
            {
                kind: 'artifact',
                title: 'Outreach report',
                meta: '222 rows · 13 columns',
            },
        ],
    },
    {
        id: 'memory',
        title: 'Teach Phoebe a preference',
        tip: 'Tag a caregiver or client with @ and name their preference. Phoebe applies it from then on.',
        prompt: [
            { text: 'Remember that ' },
            { slot: '@ pick a caregiver' },
            { text: ' prefers weekday mornings only' },
        ],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Remember that ' },
                    { pill: { kind: 'caregiver', label: 'Mara T.' } },
                    { text: ' prefers weekday mornings only' },
                ],
            },
            { kind: 'chip', text: 'Saved caregiver memory' },
            {
                kind: 'phoebe',
                text: 'Got it. I’ll use this when matching Mara.',
            },
        ],
    },
    {
        id: 'watch',
        title: 'Have Phoebe watch something',
        tip: 'Phoebe keeps checking whatever you’re waiting on, like an outreach that has gone quiet.',
        prompt: [
            {
                text:
                    'Monitor this outreach and add 10 more caregivers ' +
                    'if no one responds in 30 minutes',
            },
        ],
        // Monitoring only attaches to an outreach that still has pending
        // shifts, so this lesson picks up mid-outreach — the state a
        // scheduler is actually in when they type this. The user turn stays
        // first because the composer animation types turns[0].
        turns: [
            {
                kind: 'user',
                segments: [
                    {
                        text:
                            'Monitor this outreach and add 10 more ' +
                            'caregivers if no one responds in 30 minutes',
                    },
                ],
            },
            { kind: 'chip', text: 'Monitoring started' },
            {
                kind: 'phoebe',
                text:
                    'Watching the 12 caregivers already contacted. If it’s ' +
                    'quiet at 2:35 PM I’ll add 10 more.',
            },
            { kind: 'divider', text: '30 minutes later' },
            {
                kind: 'phoebe',
                text: 'Nobody replied, so I contacted 10 more caregivers.',
            },
            {
                kind: 'phoebe',
                text: 'Mara T. accepted Rosa Y.’s Saturday 8:00 AM shift.',
            },
        ],
    },
    {
        id: 'fill',
        title: 'Fill an open shift',
        tip: 'Phoebe ranks the best-fit caregivers first. Nobody is contacted until you start the outreach.',
        prompt: [{ text: 'Find coverage for ' }, { slot: '@ pick a shift' }],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Find coverage for ' },
                    {
                        pill: {
                            kind: 'shift',
                            label: 'Jul 11, 8:00 AM',
                            query: '@Ro',
                            pickerClientName: 'Rosa Y.',
                        },
                    },
                ],
            },
            {
                kind: 'approval',
                title: 'Create this outreach?',
                body: 'Phoebe will create an outreach for 1 shift and prepare caregiver recommendations. No caregivers are contacted until the outreach is started.',
            },
            {
                kind: 'phoebe',
                text: 'Created. I’m ranking caregivers now and will report back.',
            },
            { kind: 'divider', text: 'A minute later' },
            {
                kind: 'phoebe',
                text: 'Top matches for Rosa’s Saturday shift:',
                lines: [
                    '1. Mara T. · 0.8 mi away, free Saturday',
                    '2. Ana K. · worked with Rosa before',
                    '3. Tom Q. · matches all required skills',
                ],
            },
            {
                kind: 'approval',
                title: 'Start this outreach?',
                body: 'Phoebe will start calling and texting 3 caregivers for this outreach.',
            },
            { kind: 'chip', text: 'Outreach started' },
        ],
    },
    {
        id: 'callout',
        title: 'Handle a callout',
        tip: 'One message covers the callout and the search for a replacement.',
        prompt: [
            { slot: '@ pick a caregiver' },
            { text: ' called out for ' },
            { slot: '@ pick a shift' },
        ],
        turns: [
            {
                kind: 'user',
                segments: [
                    { pill: { kind: 'caregiver', label: 'Mara T.' } },
                    { text: ' called out for ' },
                    {
                        pill: {
                            kind: 'shift',
                            label: 'Jul 11, 8:00 AM',
                            query: '@Ro',
                            pickerClientName: 'Rosa Y.',
                        },
                    },
                ],
            },
            {
                kind: 'phoebe',
                text:
                    'Got it. Want me to open a callout and find a ' +
                    'replacement?',
            },
            {
                kind: 'approval',
                title: 'Create this outreach?',
                body: 'Phoebe will create an outreach for 1 shift and prepare caregiver recommendations. No caregivers are contacted until the outreach is started.',
            },
            {
                kind: 'phoebe',
                text: 'Callout open. I’m lining up replacements now.',
            },
        ],
    },
    {
        id: 'outreach',
        title: 'Check outreach',
        tip: 'No digging through texts. Phoebe keeps the current tally.',
        prompt: [
            { text: 'Who has responded to the outreach for ' },
            { slot: '@ pick a shift' },
            { text: '?' },
        ],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Who has responded to the outreach for ' },
                    {
                        pill: {
                            kind: 'shift',
                            label: 'Jul 11, 8:00 AM',
                            query: '@Ro',
                            pickerClientName: 'Rosa Y.',
                        },
                    },
                    { text: '?' },
                ],
            },
            {
                kind: 'phoebe',
                text:
                    '2 accepted, 1 declined, 4 no answer yet. Mara T. and ' +
                    'Ana K. can take it.',
            },
        ],
    },
    {
        id: 'scheduled',
        title: 'Schedule outreach for later',
        tip: 'Approve it now and it runs on time without you.',
        prompt: [
            { text: 'Start the outreach for ' },
            { slot: '@ pick a shift' },
            { text: ' tomorrow at 9:00 AM' },
        ],
        turns: [
            {
                kind: 'user',
                segments: [
                    { text: 'Start the outreach for ' },
                    {
                        pill: {
                            kind: 'shift',
                            label: 'Jul 11, 8:00 AM',
                            query: '@Ro',
                            pickerClientName: 'Rosa Y.',
                        },
                    },
                    { text: ' tomorrow at 9:00 AM' },
                ],
            },
            {
                kind: 'approval',
                title: 'Create this outreach?',
                body: 'Phoebe will create an outreach for 1 shift and prepare caregiver recommendations. No caregivers are contacted until Jul 12, 9:00 AM, when the outreach starts automatically.',
            },
            { kind: 'chip', text: 'Outreach scheduled' },
            {
                kind: 'phoebe',
                text: 'I’ll start it at 9:00 AM and tell you who responds.',
            },
        ],
    },
];

export type Faq = {
    readonly question: string;
    readonly answer: string;
};

export const FAQS: ReadonlyArray<Faq> = [
    {
        question: 'What can I ask Phoebe to do in the chat?',
        answer:
            'Ask for the outcome you want: find open shifts, recommend ' +
            'caregivers, draft and start outreach, check responses, send ' +
            'texts and calls, remember preferences, and pull quick reports.',
    },
    {
        question: 'There are two chat buttons. Which one is Phoebe?',
        answer:
            'The Phoebe logo in the bottom right. Use it to work on ' +
            'shifts, caregivers, and outreach, or to ask how something ' +
            'works. The bubble in the bottom left opens support, where a ' +
            'person answers anything the chat cannot.',
    },
    {
        question: 'Will Phoebe text or call caregivers without asking me?',
        answer:
            'No. Every text, call, outreach start, or record change shows ' +
            'an approval card first. For routine actions, the Allow menu ' +
            'has a “Don’t ask again” option.',
    },
    {
        question: 'Can Phoebe recommend caregivers for a shift?',
        answer:
            'Yes. Phoebe weighs availability, distance, skills, client ' +
            'history, and the preferences you’ve taught. It can also ' +
            'filter on specifics like language or driving.',
    },
    {
        question: 'How do I fix something Phoebe remembered wrong?',
        answer:
            'Ask Phoebe to correct it, the same way you taught it. The ' +
            'newest note wins and takes a few minutes to apply.',
    },
    {
        question:
            'Why can’t Phoebe see a change I just made in my scheduling system?',
        answer:
            'It probably hasn’t synced over yet. Ask for a sync, approve ' +
            'it, then give it a few minutes.',
    },
    {
        question: 'How do I request a new feature?',
        answer:
            'Ask in chat, for example “send a product feature request to ' +
            'make outreach reports filterable by office.”',
    },
];
